import { CheckCircle } from 'iconoir-react';
import type { StreamTodoItem, StreamTodoStatus } from '../types/chat';
import { cn } from 'lib/utils';

const ICON_BOX =
  'mt-[0.2rem] flex size-5 shrink-0 items-center justify-center';
const TODO_CHECK_SIZE = 18;
const TODO_CHECK_STROKE = 1.75;

interface StreamTodoListProps {
  todos: StreamTodoItem[];
  className?: string;
}

function getTodoTextClass(status: StreamTodoStatus): string {
  if (status === 'completed') {
    return 'font-normal text-[#949494] line-through dark:text-muted-foreground/70';
  }
  if (status === 'in_progress') {
    return 'font-medium text-foreground text-shimmer dark:text-foreground';
  }
  return 'font-normal text-[#D4D4D4] dark:text-foreground/85';
}

function TodoStatusIcon({ status }: { readonly status: StreamTodoStatus }) {
  if (status === 'completed') {
    return (
      <CheckCircle
        width={TODO_CHECK_SIZE}
        height={TODO_CHECK_SIZE}
        strokeWidth={TODO_CHECK_STROKE}
        className="text-[#949494] dark:text-muted-foreground/70"
        aria-hidden
      />
    );
  }

  if (status === 'in_progress') {
    return (
      <span
        className="size-2 rounded-full bg-amber-500 ring-[3px] ring-amber-500/25"
        aria-hidden
      />
    );
  }

  return (
    <span
      className="size-2.5 rounded-full border-2 border-[#9C9C9E] dark:border-muted-foreground/55"
      aria-hidden
    />
  );
}

export function StreamTodoList({ todos, className }: Readonly<StreamTodoListProps>) {
  if (!todos.length) {
    return null;
  }
  return (
    <div className={cn('flex flex-col pt-2 mb-3', className)}>
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-[#9C9C9E] dark:text-muted-foreground/90">
        Todo
      </p>

      <ul className="m-0 flex list-none flex-col gap-3.5 p-0">
        {todos.map((todo, index) => (
          <li key={`${todo.content}-${index}`} className="flex items-start gap-3">
            <span className={ICON_BOX}>
              <TodoStatusIcon status={todo.status} />
            </span>
            <span
              className={cn(
                'min-w-0 text-[0.9375rem] leading-[1.55]',
                getTodoTextClass(todo.status),
              )}
            >
              {todo.content}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
